

import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Stack,
  Avatar,
  alpha,
  useTheme,
  CircularProgress,
  Button,
  Paper,
  Skeleton,
} from "@mui/material";
import {
  MdGroup,
  MdToday,
  MdDateRange,
  MdTrendingUp,
  MdPerson,
  MdRefresh,
  MdError,
  MdLocationCity,
} from "react-icons/md";
import axios from "axios";

const Usertotal = ({ setType, type }) => {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Medstown brand colors
  const medstownColors = {
    primary: "#014D4D",
    secondary: "#02A0A0",
    accent: "#FFB800",
    success: "#4caf50",
    error: "#f44336",
    warning: "#ff9800",
  };

  async function getData() {
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get(`https://api.medstown.com/customer/userAnylatics`);
      if (res.data) {
        setData(res.data);
      } else {
        setError(true);
      }
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getData();
  }, []);

  // Define cards configuration with compact styling
  const cardConfigs = [
    {
      id: "all",
      title: "Total Users",
      value: data?.total_user || 0,
      icon: MdGroup,
      color: medstownColors.primary,
      gradient: `linear-gradient(135deg, ${medstownColors.primary}, ${alpha(medstownColors.primary, 0.8)})`,
    },
    {
      id: "active",
      title: "Today",
      value: data?.today || 0,
      icon: MdToday,
      color: medstownColors.success,
      gradient: `linear-gradient(135deg, ${medstownColors.success}, ${alpha(medstownColors.success, 0.8)})`,
    },
    {
      id: "requested",
      title: "This Month",
      value: data?.month || 0,
      icon: MdDateRange,
      color: medstownColors.secondary,
      gradient: `linear-gradient(135deg, ${medstownColors.secondary}, ${alpha(medstownColors.secondary, 0.8)})`,
    },
  ];

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 1.5, // Reduced padding
          borderRadius: 2, // Smaller border radius
          border: `1px solid ${alpha(medstownColors.primary, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha('#ffffff', 0.9)}, ${alpha('#f8f9fa', 0.9)})`
        }}
      >
        <Grid container spacing={1.5}> {/* Reduced spacing */}
          {[1, 2, 3].map((item) => (
            <Grid item xs={12} md={4} key={item}>
              <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <CardContent sx={{ p: 1.5 }}> {/* Reduced padding */}
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Skeleton variant="circular" width={32} height={32} /> {/* Smaller skeleton */}
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="60%" height={16} />
                      <Skeleton variant="text" width="40%" height={12} />
                    </Box>
                  </Stack>
                  <Skeleton variant="text" width="30%" height={28} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2, // Reduced padding
          borderRadius: 2,
          border: `1px solid ${alpha(medstownColors.error, 0.2)}`,
          background: `linear-gradient(135deg, ${alpha(medstownColors.error, 0.05)}, ${alpha(medstownColors.error, 0.02)})`
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{
            backgroundColor: alpha(medstownColors.error, 0.1),
            color: medstownColors.error,
            width: 40, // Smaller avatar
            height: 40
          }}>
            <MdError size={20} /> {/* Smaller icon */}
          </Avatar>
          
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" fontWeight={600} color="error.main" sx={{ mb: 0.5, fontSize: '0.9rem' }}>
              Failed to Load Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.75rem' }}>
              Unable to fetch user analytics data.
            </Typography>
            <Button
              variant="contained"
              size="small" // Smaller button
              startIcon={<MdRefresh size={14} />}
              onClick={() => {
                setError(false);
                getData();
              }}
              sx={{
                backgroundColor: medstownColors.error,
                '&:hover': { backgroundColor: alpha(medstownColors.error, 0.8) },
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem' // Smaller font
              }}
            >
              Retry
            </Button>
          </Box>
        </Stack>
      </Paper>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5, // Reduced padding
        borderRadius: 2, // Smaller border radius
        border: `1px solid ${alpha(medstownColors.primary, 0.08)}`,
        background: `linear-gradient(135deg, ${alpha('#ffffff', 0.95)}, ${alpha('#f8f9fa', 0.95)})`,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '100%',
          height: '100%',
          opacity: 0.02, // More subtle
          background: `
            radial-gradient(circle at 20% 80%, ${medstownColors.primary} 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, ${medstownColors.secondary} 0%, transparent 50%)
          `,
        }}
      />

      {/* Compact Analytics Cards Grid */}
      <Grid container spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}> {/* Reduced spacing */}
        {cardConfigs.map((config) => (
          <Grid item xs={12} md={4} key={config.id}>
            <Card
              onClick={() => {
                if (type !== config.id && setType) {
                  setType(config.id);
                }
              }}
              sx={{
                borderRadius: 2, // Smaller border radius
                overflow: 'hidden',
                cursor: setType ? 'pointer' : 'default',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: `2px solid ${type === config.id ? config.color : 'transparent'}`,
                boxShadow: type === config.id 
                  ? `0 4px 16px ${alpha(config.color, 0.2)}` // Reduced shadow
                  : `0 2px 8px ${alpha(theme.palette.common.black, 0.06)}`,
                '&:hover': setType ? {
                  transform: 'translateY(-3px)', // Reduced transform
                  boxShadow: `0 6px 20px ${alpha(config.color, 0.2)}`,
                  border: `2px solid ${alpha(config.color, 0.5)}`,
                } : {},
                position: 'relative',
              }}
            >
              {/* Card Background Gradient */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100%',
                  height: '100%',
                  background: type === config.id 
                    ? `linear-gradient(135deg, ${alpha(config.color, 0.06)}, ${alpha(config.color, 0.02)})`
                    : 'transparent',
                  opacity: 0.8,
                }}
              />

              <CardContent sx={{ p: 1.5, position: 'relative', zIndex: 1 }}> {/* Reduced padding */}
                {/* Compact Card Header */}
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}> {/* Reduced spacing */}
                  <Avatar
                    sx={{
                      background: config.gradient,
                      color: 'white',
                      width: 32, // Smaller avatar
                      height: 32,
                      boxShadow: `0 2px 8px ${alpha(config.color, 0.2)}`, // Reduced shadow
                    }}
                  >
                    <config.icon size={16} /> {/* Smaller icon */}
                  </Avatar>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="caption"
                      fontWeight={600} 
                      color="text.secondary"
                      sx={{ 
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        fontSize: '0.65rem', // Smaller font
                        mb: 0.2
                      }}
                    >
                      {config.title}
                    </Typography>
                  </Box>
                </Stack>

                {/* Compact Main Value Display */}
                <Box sx={{ textAlign: 'left' }}>
                  <Typography 
                    variant="h4" // Smaller heading
                    fontWeight={800}
                    sx={{
                      color: type === config.id ? config.color : 'text.primary',
                      mb: 0.5, // Reduced margin
                      lineHeight: 1,
                      fontSize: '1.8rem', // Explicit smaller size
                      textShadow: type === config.id ? `0 1px 2px ${alpha(config.color, 0.2)}` : 'none',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {config.value?.toLocaleString() || '0'}
                  </Typography>

                 
                </Box>
              </CardContent>

              {/* Selection Indicator */}
              {type === config.id && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: 3, // Reduced height
                    background: config.gradient,
                  }}
                />
              )}
            </Card>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default React.memo(Usertotal);
